<template>
  <div class="page-container">
    <div class="list-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <PageHeader :config="config" @module-click="handleModuleClick" />
      <Breadcrumb :items="breadcrumbItems" />

      <SearchBanner
        search-object="valueAssessment"
        :title-main1="headerConfig.titleMain1"
        :title-main2="headerConfig.titleMain2"
        :subtitle="headerConfig.titleSub"
        :placeholder="headerConfig.searchPlaceholder"
        :button-text="headerConfig.searchButton"
        :keyword.sync="searchKeyword"
        :background-image="bannerBg"
        @search="handleSearch"
      />

      <div class="system-section content-wrap">
        <HomeSectionTitle
          class="system-heading"
          :title="systemSection.title"
          :subtitle="systemSection.subtitle"
          :deco-src="systemSection.nameDeco"
        />
        <div class="system-grid">
          <div
            v-for="item in systemItems"
            :key="item.key"
            class="system-card"
            :style="systemCardStyle(item)"
          >
            <div class="system-card-inner">
              <div class="system-head">
                <img
                  v-if="item.icon"
                  class="system-ico"
                  :src="getSafeImage(item.icon)"
                  alt=""
                  @error="onImgError"
                />
                <span class="system-name">{{ item.title }}</span>
              </div>
              <p class="system-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="filter-panel content-wrap">
        <div class="filter-bar">
          <img class="filter-ico" :src="iconField" alt="" @error="onImgError" />
          <span class="filter-label">
            <span class="filter-label-text">{{ config.fieldFilter?.label }}</span>
            <span class="filter-colon">：</span>
          </span>
          <div class="filter-chips">
            <button
              v-for="tab in fieldTabs"
              :key="'f-' + (tab.value || 'all')"
              type="button"
              class="filter-chip"
              :class="{ active: activeField === tab.value }"
              @click="handleFieldChange(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <div class="filter-bar">
          <img class="filter-ico" :src="iconTime" alt="" @error="onImgError" />
          <span class="filter-label">
            <span class="filter-label-text">{{ config.timeFilter?.label }}</span>
            <span class="filter-colon">：</span>
          </span>
          <div class="filter-chips">
            <button
              v-for="tab in timeTabs"
              :key="'t-' + (tab.value || 'all')"
              type="button"
              class="filter-chip"
              :class="{ active: activeTime === tab.value }"
              @click="handleTimeChange(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <div class="filter-bar">
          <img class="filter-ico" :src="iconScore" alt="" @error="onImgError" />
          <span class="filter-label">
            <span class="filter-label-text">{{ config.scoreFilter?.label }}</span>
            <span class="filter-colon">：</span>
          </span>
          <div class="filter-chips">
            <button
              v-for="tab in scoreTabs"
              :key="'s-' + (tab.value || 'all')"
              type="button"
              class="filter-chip"
              :class="{ active: activeScore === tab.value }"
              @click="handleScoreChange(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <div class="filter-bar">
          <img class="filter-ico" :src="iconMaturity" alt="" @error="onImgError" />
          <span class="filter-label">
            <span class="filter-label-text">{{ config.maturityFilter?.label }}</span>
            <span class="filter-colon">：</span>
          </span>
          <div class="filter-chips">
            <button
              v-for="tab in maturityTabs"
              :key="'m-' + (tab.value || 'all')"
              type="button"
              class="filter-chip"
              :class="{ active: activeMaturity === tab.value }"
              @click="handleMaturityChange(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="list-wrap content-wrap">
        <div v-if="loading" class="section-empty">{{ paginationConfig.loading }}</div>
        <div v-else-if="list.length" class="card-grid">
          <ValueAssessmentCard
            v-for="item in list"
            :key="item.id"
            :item="item"
            @click="goDetail"
          />
        </div>
        <div v-else class="section-empty">{{ emptyText }}</div>

        <div v-if="totalCount > 0" class="pagination">
          <span class="pagination-info">{{ pageInfoText }}</span>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">
              {{ paginationConfig.prevPage }}
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              type="button"
              class="page-number"
              :class="{ active: currentPage === page }"
              @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">
              {{ paginationConfig.nextPage }}
            </button>
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { getValueAssessmentNewConfig } from '@/config/valueAssessmentNewConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import HomeSectionTitle from '@/components/HomeSectionTitle.vue'
import ValueAssessmentCard from '@/components/valueAssessment/ValueAssessmentCard.vue'
import valueAssessmentApi from '@/api/valueAssessment'
import supplyDemandApi from '@/api/supplyDemand'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getText, getCurrentLocale } from '@/utils/i18nText'
import { CATEGORY_ALL_LABEL } from '@/utils/filterTabs'

const VA_FILTER_DICT_CODES = {
  field: 'yField',
  time: 'ylValuationPeriod',
  score: 'ylValuationScore',
  maturity: 'ylTechnologyReadinessLevel'
}

export default {
  name: 'ValueAssessmentNew',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner,
    HomeSectionTitle,
    ValueAssessmentCard
  },
  data() {
    return {
      config: {},
      searchKeyword: '',
      breadcrumbItems: [],
      fieldTabs: [],
      timeTabs: [],
      scoreTabs: [],
      maturityTabs: [],
      activeField: '',
      activeTime: '',
      activeScore: '',
      activeMaturity: '',
      list: [],
      loading: false,
      currentPage: 1,
      pageSize: 12,
      totalCount: 0
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    emptyText() {
      return getText('common.empty') || '暂无数据'
    },
    headerConfig() {
      return this.config.header || {}
    },
    paginationConfig() {
      return this.config.pagination || {}
    },
    systemSection() {
      return this.config.systemSection || {}
    },
    systemItems() {
      return Array.isArray(this.systemSection.items) ? this.systemSection.items : []
    },
    bannerBg() {
      return getSafeImage(this.headerConfig.bannerBg)
    },
    iconField() {
      return getSafeImage(this.config.icons?.field)
    },
    iconTime() {
      return getSafeImage(this.config.icons?.time)
    },
    iconScore() {
      return getSafeImage(this.config.icons?.score)
    },
    iconMaturity() {
      return getSafeImage(this.config.icons?.maturity)
    },
    totalPages() {
      const pages = Math.ceil(this.totalCount / this.pageSize)
      return pages > 0 ? pages : 1
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1)
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },
    pageInfoText() {
      const tpl = this.paginationConfig.pageInfo || '共{total}条，每页显示{pageSize}条'
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    }
  },
  created() {
    this.loadConfig()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    getSafeImage,
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },
    getPageScaleEl() {
      return this.$el.querySelector('.list-page')
    },
    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    onImgError(e) {
      handleImgError(e)
    },
    systemCardStyle(item) {
      const src = getSafeImage(item?.bg)
      if (!src) return null
      return {
        backgroundImage: `url(${src})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right center',
        backgroundSize: 'cover'
      }
    },
    async loadConfig() {
      this.config = getValueAssessmentNewConfig(getCurrentLocale())
      const bc = this.config.breadcrumb || {}
      this.breadcrumbItems = [
        { label: bc.home || '', path: '/index' },
        { label: bc.current || '', path: '' }
      ]
      const kw = this.$route?.query?.keyword
      if (kw) this.searchKeyword = String(kw)
      await this.loadFilterDictData()
      this.loadList()
    },
    parseDictRows(result) {
      if (!result || result.status !== 0) return []
      if (result.data && Array.isArray(result.data.rows)) return result.data.rows
      if (Array.isArray(result.data)) return result.data
      if (typeof result.data === 'object' && result.data !== null) {
        return Object.values(result.data).flat()
      }
      return []
    },
    buildFlatFilterOptions(rows, dictCode, allLabel) {
      const items = (rows || [])
        .filter(item => item && item.dictCode === dictCode)
        .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
      return [
        { label: allLabel || CATEGORY_ALL_LABEL, value: '' },
        ...items.map(item => ({
          label: item.itemValue || item.itemCode || '',
          value: String(item.itemCode || '')
        }))
      ]
    },
    async loadFilterDictData() {
      const fieldAll = this.config.fieldFilter?.allLabel || CATEGORY_ALL_LABEL
      const timeAll = this.config.timeFilter?.allLabel || CATEGORY_ALL_LABEL
      const scoreAll = this.config.scoreFilter?.allLabel || CATEGORY_ALL_LABEL
      const maturityAll = this.config.maturityFilter?.allLabel || CATEGORY_ALL_LABEL
      try {
        const response = await supplyDemandApi.getDictData(Object.values(VA_FILTER_DICT_CODES))
        const rows = this.parseDictRows(response.data || {})
        this.fieldTabs = this.buildFlatFilterOptions(rows, VA_FILTER_DICT_CODES.field, fieldAll)
        this.timeTabs = this.buildFlatFilterOptions(rows, VA_FILTER_DICT_CODES.time, timeAll)
        this.scoreTabs = this.buildFlatFilterOptions(rows, VA_FILTER_DICT_CODES.score, scoreAll)
        this.maturityTabs = this.buildFlatFilterOptions(rows, VA_FILTER_DICT_CODES.maturity, maturityAll)
      } catch (e) {
        console.error('加载价值评估筛选字典失败:', e)
        this.fieldTabs = [{ label: fieldAll, value: '' }]
        this.timeTabs = [{ label: timeAll, value: '' }]
        this.scoreTabs = [{ label: scoreAll, value: '' }]
        this.maturityTabs = [{ label: maturityAll, value: '' }]
      }
      this.activeField = this.fieldTabs[0]?.value ?? ''
      this.activeTime = this.timeTabs[0]?.value ?? ''
      this.activeScore = this.scoreTabs[0]?.value ?? ''
      this.activeMaturity = this.maturityTabs[0]?.value ?? ''
    },
    getFilterQueryParams() {
      const params = {}
      if (this.activeField) params.field = this.activeField
      if (this.activeTime) params.valuationPeriod = this.activeTime
      if (this.activeScore) params.valuationScore = this.activeScore
      if (this.activeMaturity) params.technologyReadinessLevel = this.activeMaturity
      return params
    },
    formatArchiveDate(archiveTime) {
      if (!archiveTime) return ''
      const d = new Date(archiveTime)
      if (Number.isNaN(d.getTime())) {
        const raw = String(archiveTime)
        return raw.length >= 10 ? raw.slice(0, 10) : raw
      }
      const m = `${d.getMonth() + 1}`.padStart(2, '0')
      const day = `${d.getDate()}`.padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },
    resolveMaturityLabel(item) {
      const label =
        item.technologyReadinessLevelLabel ||
        item.maturityLabel ||
        item.trlLabel ||
        ''
      if (label) return label
      const code = item.technologyReadinessLevel || item.trl || ''
      if (!code) return ''
      const hit = this.maturityTabs.find(tab => String(tab.value) === String(code))
      return hit?.label || String(code)
    },
    pickCover(item) {
      return (
        item.coverUrl ||
        item.coverImage ||
        item.imageUrl ||
        item.picUrl ||
        item.thumbnail ||
        item.achievementCover ||
        ''
      )
    },
    mapArchiveItem(item) {
      return {
        id: item.id,
        assessmentId: item.assessmentId,
        achievementId: item.achievementId,
        templateRecordId: item.templateRecordId,
        title: item.achievementName || '',
        description: item.techFeatures || item.introduction || '',
        date: this.formatArchiveDate(item.archiveTime || item.assessmentTime || item.createTime),
        score: item.score != null && item.score !== '' ? String(item.score) : '',
        type: item.categoryLabel || item.achievementTypeLabel || '',
        field: item.fieldLabel || '',
        maturity: this.resolveMaturityLabel(item),
        cover: this.pickCover(item)
      }
    },
    async loadList() {
      this.loading = true
      try {
        const response = await valueAssessmentApi.getArchiveList({
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          achievementName: (this.searchKeyword || '').trim(),
          ...this.getFilterQueryParams()
        })
        const result = response.data || {}
        const ok = result.code === 200 || result.status === 0
        if (ok && result.data) {
          const page = result.data
          const rows = Array.isArray(page.list) ? page.list : []
          this.totalCount = Number(page.total) || 0
          this.list = rows.map(item => this.mapArchiveItem(item))
        } else {
          this.list = []
          this.totalCount = 0
        }
      } catch (e) {
        console.error('加载价值评估列表失败:', e)
        this.list = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    onFilterChange() {
      this.currentPage = 1
      this.loadList()
    },
    handleFieldChange(value) {
      this.activeField = value
      this.onFilterChange()
    },
    handleTimeChange(value) {
      this.activeTime = value
      this.onFilterChange()
    },
    handleScoreChange(value) {
      this.activeScore = value
      this.onFilterChange()
    },
    handleMaturityChange(value) {
      this.activeMaturity = value
      this.onFilterChange()
    },
    handleSearch() {
      this.currentPage = 1
      this.loadList()
    },
    handleModuleClick() {},
    goDetail(item) {
      this.$router.push({
        path: '/value-assessment/detail',
        query: {
          id: item.id,
          templateRecordId: item.templateRecordId,
          assessmentId: item.assessmentId,
          achievementId: item.achievementId,
          title: item.title
        }
      })
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadList()
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadList()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background: #fff;
}

.list-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

.content-wrap {
  width: 1700px;
  margin-left: 110px;
  margin-right: auto;
  box-sizing: border-box;
}

::v-deep .header-banner {
  margin-bottom: 0;

  .title-main-1,
  .title-main-2,
  .title-main-single {
    color: #8c7c26;
  }

  .title-sub {
    color: #a0903c;
  }
}

.system-section {
  margin-top: 28px;
  margin-bottom: 8px;
}

.system-heading {
  margin-bottom: 18px;
}

::v-deep .system-heading {
  .home-section-title__cn {
    font-size: 28px;
    font-weight: 700;
    color: #333;
    letter-spacing: 1px;
  }

  .home-section-title__en {
    font-size: 12px;
    color: #a0903c;
    letter-spacing: 1px;
  }

  .home-section-title__deco {
    width: 22px;
    height: 18px;
  }
}

.system-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.system-card {
  min-height: 148px;
  border-radius: 4px;
  background-color: #f5f8fb;
  overflow: hidden;
  box-sizing: border-box;
}

.system-card-inner {
  height: 100%;
  padding: 22px 20px 18px;
  box-sizing: border-box;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.78) 0%, rgba(255, 255, 255, 0.35) 62%, rgba(255, 255, 255, 0) 100%);
}

.system-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.system-ico {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}

.system-name {
  font-size: 18px;
  font-weight: 700;
  color: #222;
}

.system-desc {
  margin: 12px 0 0;
  max-width: 82%;
  font-size: 13px;
  line-height: 1.7;
  color: #666;
  white-space: pre-line;
}

.filter-panel {
  margin-top: 20px;
  margin-bottom: 18px;
}

.filter-bar {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 24px;
  background: rgba(160, 144, 60, 0.1);
  box-sizing: border-box;

  & + .filter-bar {
    background: rgba(160, 144, 60, 0.06);
  }
}

.filter-ico {
  width: 16px;
  height: 16px;
  margin-top: 6px;
  flex-shrink: 0;
  object-fit: contain;
}

.filter-label {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 5em;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  color: #a0903c;
  line-height: 1.4;
  box-sizing: border-box;
}

.filter-label-text {
  flex: 1;
  min-width: 0;
  height: 1.4em;
  text-align: justify;
  text-align-last: justify;
  overflow: hidden;
}

.filter-colon {
  flex-shrink: 0;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: 8px 12px;
  text-align: left;
}

.filter-chip {
  height: 28px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 2px;
  background: transparent;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  line-height: 26px;
  box-sizing: border-box;

  &.active {
    background: #f0ebe0;
    border: 1px solid #e2d9c0;
    color: #333;
    font-weight: 600;
  }

  &:hover:not(.active) {
    color: #8c7c26;
  }
}

.list-wrap {
  margin-bottom: 40px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
}

.section-empty {
  width: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  background: #fff;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 28px;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn,
.page-number {
  min-width: 36px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled):not(.active) {
    border-color: #a0903c;
    color: #8a7a28;
  }

  &.active {
    border-color: #666;
    color: #fff;
    background: #666;
  }
}


.locale-zh-CN {
  .filter-label,
  .field-label {
    font-size: 16px;
  }

  .filter-chip,
  .field-chip {
    font-size: 18px;
    height: 36px;
    line-height: 34px;
    padding: 0 20px;
  }

  .filter-ico,
  .field-ico {
    width: 18px;
    height: 18px;
  }

  .system-name {
    font-size: 20px;
  }

  .system-desc {
    font-size: 15px;
  }

  .system-ico {
    width: 32px;
    height: 32px;
  }

  ::v-deep .system-heading {
    .home-section-title__cn {
      font-size: 30px;
    }

    .home-section-title__en {
      font-size: 14px;
    }

    .home-section-title__deco {
      width: 24px;
      height: 20px;
    }
  }

  .pagination-info {
    font-size: 15px;
  }

  .page-btn,
  .page-number {
    min-width: 40px;
    height: 36px;
    font-size: 14px;
  }
}

</style>
